import { themeAtom } from "../../shared/atoms";
import { jaroWinkler } from "jaro-winkler-typescript";
import { useAtomValue } from "jotai";
import React, { forwardRef, ReactNode, useEffect, useImperativeHandle, useState } from "react";
import { twMerge } from "tailwind-merge";

// Percentage-based comparison to handle discrepancies in voyage status values between Snowflake FE values, reducing maintenance effort for potential future changes.
const DIFF_THRESHOLD = 0.93;

interface TabProps {
  label: string;
  children: ReactNode;
  active?: boolean;
}

const Tab: React.FC<TabProps> = ({ label, children, active }) => {
  const theme = useAtomValue(themeAtom);

  return (
    <div className="relative">
      <div
        data-label={label}
        className={twMerge(
          "cursor-pointer border",
          active ? "bg-green text-green" : twMerge("border-transparent", theme.home.tailwind.textDefault),
        )}
      >
        {children}
      </div>
    </div>
  );
};

interface TabsProps {
  children: ReactNode;
  variant?: "default" | "voyageChecklist";
  onActiveTabChange?: (label: string) => void;
}

export type TabsHandle = {
  setActiveTab: (label: string) => void;
};

const findMatchingTabLabel = (children: ReactNode, label: string) =>
  (children as React.ReactElement<TabProps>[]).find(
    (child) => jaroWinkler(child.props.label, label, { caseSensitive: false }) > DIFF_THRESHOLD,
  )?.props.label;

const Tabs = forwardRef<TabsHandle, TabsProps>(
  ({ children, variant = "default", onActiveTabChange }: TabsProps, ref) => {
    const theme = useAtomValue(themeAtom);

    const [tabActiveButton, setTabActiveButton] = useState("");

    useImperativeHandle(ref, () => ({
      setActiveTab: (label: string) => {
        setTabActiveButton(label);
        const initialActiveTab = findMatchingTabLabel(children, label);
        if (initialActiveTab) {
          setActiveTab(initialActiveTab);
        }
      },
    }));

    const [activeTab, setActiveTab] = useState((children as React.ReactElement<TabProps>[])[0].props.label);

    const isNowVisible = tabActiveButton !== "closed";

    const handleClick = (newActiveTab: string) => {
      setActiveTab(newActiveTab);
    };

    const isVoyageChecklist = variant === "voyageChecklist";

    useEffect(() => {
      onActiveTabChange?.(activeTab);
    }, [activeTab, onActiveTabChange]);

    return (
      <div>
        <div
          className={twMerge(
            "flex gap-1",
            isVoyageChecklist &&
              twMerge(
                "mt-2 overflow-x-auto rounded-2xl p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                theme.voyageChecklist.tabsContainerBg,
              ),
            !isVoyageChecklist && "mt-4",
          )}
        >
          {(children as React.ReactElement<TabProps>[]).map((child) => {
            const isMatch = jaroWinkler(child.props.label, tabActiveButton, { caseSensitive: false }) > DIFF_THRESHOLD;
            const isNow = isNowVisible && isMatch;
            const isActive = activeTab === child.props.label;

            return (
              <button
                key={child.props.label}
                onClick={() => handleClick(child.props.label)}
                type="button"
                aria-pressed={isActive}
                data-state={isActive ? "active" : "inactive"}
                className={twMerge(
                  "relative flex items-center justify-center text-[11.5px] transition-all duration-150 focus:outline-none",
                  isVoyageChecklist
                    ? "min-h-[56px] min-w-max flex-1 rounded-[14px] border px-2"
                    : "h-12 flex-1 rounded border border-transparent",
                  isVoyageChecklist
                    ? isActive
                      ? twMerge(
                          "font-semibold shadow-[0_1px_2px_rgba(16,24,40,0.06)]",
                          theme.myVesselsTable.tailwind.tableBorder,
                          theme.home.tailwind.panelBackground,
                          theme.home.tailwind.textDefault,
                        )
                      : twMerge(
                          "border-transparent bg-transparent font-medium",
                          theme.voyageChecklist.tabInactiveText,
                          theme.voyageChecklist.tabInactiveHoverText,
                        )
                    : isActive
                      ? "border-green font-semibold"
                      : theme.uwhcAssistant.border,
                  !isVoyageChecklist && theme.home.tailwind.pageBackground,
                  !isVoyageChecklist && theme.home.tailwind.textDefault,
                )}
              >
                {isNow && !isVoyageChecklist && (
                  <div
                    className={twMerge(
                      "absolute -top-3 rounded bg-green px-1.5 py-1 font-bold",
                      theme.uwhcAssistant.sendText,
                    )}
                  >
                    Now
                  </div>
                )}
                <span className={twMerge(isVoyageChecklist && "truncate whitespace-nowrap leading-5")}>
                  {child.props.label}
                </span>
              </button>
            );
          })}
        </div>
        <div className={twMerge(isVoyageChecklist ? "pt-4" : "py-4")}>
          {(children as React.ReactElement<TabProps>[]).map((child) => (
            <div
              key={child.props.label}
              data-active={activeTab === child.props.label}
              className={activeTab === child.props.label ? "voyage-checklist-panel" : "hidden"}
            >
              {child.props.children}
            </div>
          ))}
        </div>
      </div>
    );
  },
);

export { Tab, Tabs };
